import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import { Loader2, Plus, X } from 'lucide-react';

import {
  getPatientTags,
  getPatientTagDefinitions,
  replacePatientTags,
  type PatientTagDefinitionResponse,
  type PatientTagResponse,
} from '../../services/patientTagsApi';
import { useToast } from '../../hooks/useToastContext';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

interface PatientTagsSectionProps {
  patientId: string;
  clinicId?: string | null;
  canEdit: boolean;
}

/**
 * Patient Info tag editing is intentionally separate from demographic
 * autosave. Definitions come only from the patient's current clinic; assigned
 * rows still include archived labels so a clinician can remove an old tag
 * without making it selectable again.
 */
export const PatientTagsSection: React.FC<PatientTagsSectionProps> = ({
  patientId,
  clinicId,
  canEdit,
}) => {
  const { error: showError } = useToast();
  const [tags, setTags] = useState<PatientTagResponse[]>([]);
  const [definitions, setDefinitions] = useState<
    PatientTagDefinitionResponse[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [addPopoverOpen, setAddPopoverOpen] = useState(false);
  const requestGeneration = useRef(0);
  const loadedIdentityRef = useRef<string | null>(null);
  const currentIdentityRef = useRef({ patientId, clinicId: clinicId ?? '' });
  currentIdentityRef.current = { patientId, clinicId: clinicId ?? '' };
  const mutationInFlight = useRef(false);

  const identityKey = `${patientId}:${clinicId ?? ''}`;

  const isCurrentIdentity = (identity: {
    patientId: string;
    clinicId: string;
  }) =>
    currentIdentityRef.current.patientId === identity.patientId &&
    currentIdentityRef.current.clinicId === identity.clinicId;

  const load = useCallback(async () => {
    const identity = { patientId, clinicId: clinicId ?? '' };
    const generation = ++requestGeneration.current;
    loadedIdentityRef.current = null;
    mutationInFlight.current = false;
    setMutatingId(null);
    setAddPopoverOpen(false);
    setLoading(true);
    setLoadError(false);
    try {
      const [nextTags, nextDefinitions] = await Promise.all([
        getPatientTags(patientId),
        clinicId
          ? getPatientTagDefinitions({
              clinicId,
              // Archived definitions are returned through assigned tags; this
              // request is only the set that may be added now.
              includeInactive: false,
            })
          : Promise.resolve([]),
      ]);
      if (
        generation !== requestGeneration.current ||
        !isCurrentIdentity(identity)
      ) {
        return;
      }
      setTags(nextTags ?? []);
      setDefinitions(nextDefinitions ?? []);
      loadedIdentityRef.current = identityKey;
    } catch (error) {
      console.error('Failed to load patient tags', error);
      if (
        generation === requestGeneration.current &&
        isCurrentIdentity(identity)
      ) {
        setLoadError(true);
        showError(
          getBusinessErrorMessage(error) ?? 'Unable to load patient tags.'
        );
      }
    } finally {
      if (
        generation === requestGeneration.current &&
        isCurrentIdentity(identity)
      ) {
        setLoading(false);
      }
    }
  }, [clinicId, identityKey, patientId, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const replaceClinicTags = async (
    targetClinicId: string,
    nextIds: string[],
    mutationId: string
  ) => {
    if (mutationInFlight.current) return;
    mutationInFlight.current = true;
    const targetIdentity = { patientId, clinicId: clinicId ?? '' };
    const generation = requestGeneration.current;
    setMutatingId(mutationId);
    try {
      const next = await replacePatientTags(patientId, {
        clinic_id: targetClinicId,
        tag_definition_ids: nextIds,
      });
      if (
        generation === requestGeneration.current &&
        isCurrentIdentity(targetIdentity)
      ) {
        setTags(next);
      }
    } catch (error) {
      console.error('Failed to save patient tag', error);
      if (
        generation === requestGeneration.current &&
        isCurrentIdentity(targetIdentity)
      ) {
        showError(
          getBusinessErrorMessage(error) ?? 'Unable to save patient tag.'
        );
      }
    } finally {
      if (
        generation === requestGeneration.current &&
        isCurrentIdentity(targetIdentity)
      ) {
        mutationInFlight.current = false;
        setMutatingId(null);
      }
    }
  };

  const add = async (definition: PatientTagDefinitionResponse) => {
    if (!clinicId) return;
    const currentIds = tags
      .filter(tag => tag.clinic_id === clinicId)
      .map(tag => tag.id);
    await replaceClinicTags(
      clinicId,
      [...currentIds, definition.id],
      definition.id
    );
  };

  const remove = async (tag: PatientTagResponse) => {
    const nextIds = tags
      .filter(other => other.clinic_id === tag.clinic_id && other.id !== tag.id)
      .map(other => other.id);
    await replaceClinicTags(tag.clinic_id, nextIds, tag.id);
  };

  const activeDefinitionsNotAssigned = definitions.filter(
    definition =>
      definition.clinic_id === clinicId &&
      !tags.some(tag => tag.clinic_id === clinicId && tag.id === definition.id)
  );

  const canAddTag =
    canEdit && Boolean(clinicId) && activeDefinitionsNotAssigned.length > 0;

  const waitingForCurrentPatient =
    !loadError && loadedIdentityRef.current !== identityKey;

  if (loading || waitingForCurrentPatient) {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' /> Loading tags...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className='space-y-2 rounded-md border p-3' role='alert'>
        <p className='text-sm text-destructive'>
          Unable to load patient tags. Please try again.
        </p>
        <Button size='sm' variant='outline' onClick={() => void load()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <section className='space-y-3 rounded-md border p-3'>
      <div>
        <h3 className='text-sm font-semibold'>Patient tags</h3>
        {canEdit && clinicId && (
          <p className='text-xs text-muted-foreground'>
            Add tags from the patient&apos;s current clinic. Archived tags
            remain on the chart until removed.
          </p>
        )}
      </div>
      {tags.length === 0 && !canAddTag ? (
        <p className='text-sm text-muted-foreground'>No tags assigned.</p>
      ) : (
        <div className='flex flex-wrap items-center gap-2'>
          {tags.map(tag => (
            <span
              key={`${tag.clinic_id}-${tag.id}`}
              className='inline-flex max-w-full items-center gap-1.5 rounded-full border bg-muted/40 px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted/60'
            >
              <span className='max-w-[12rem] truncate' title={tag.name}>
                {tag.name}
              </span>
              {!tag.is_active && <Badge variant='secondary'>Archived</Badge>}
              {canEdit && (
                <button
                  type='button'
                  className='inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground'
                  aria-label={`Remove ${tag.name}`}
                  disabled={mutatingId !== null}
                  onClick={() => void remove(tag)}
                >
                  <X className='h-3 w-3' aria-hidden='true' />
                </button>
              )}
            </span>
          ))}
          {canAddTag && (
            <Popover open={addPopoverOpen} onOpenChange={setAddPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={mutatingId !== null}
                  className='h-7 rounded-full border-dashed px-2.5 text-xs text-muted-foreground hover:text-foreground'
                >
                  <Plus className='mr-1 h-3 w-3' aria-hidden='true' />
                  {tags.length === 0 ? 'Add tag' : 'Add'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-64 p-0' align='start'>
                <Command>
                  <CommandInput placeholder='Search tags...' />
                  <CommandList>
                    <CommandEmpty>No tags found.</CommandEmpty>
                    <CommandGroup>
                      {activeDefinitionsNotAssigned.map(definition => (
                        <CommandItem
                          key={definition.id}
                          value={definition.name}
                          onSelect={() => {
                            setAddPopoverOpen(false);
                            void add(definition);
                          }}
                        >
                          <span className='truncate'>{definition.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}
    </section>
  );
};
